import {
  AnalyticEvent,
  isWeb,
  useCreateReviewMutation,
  useGetReservationByIdQuery,
  usePostHog,
} from "@stepapp/core";
import {
  Avatar,
  Block,
  Button,
  CirclePlaceholder,
  Input,
  LinePlaceholder,
  palette,
  RatingScale,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
  useHeaderHeight,
} from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet } from "react-native";
import { smallPageWrapper } from "../../../../src/styles/smallPageWrapper";
import { footerStyle } from "../../../../src/styles/themes";

type AdditionalQuestionsProps = {
  question: string;
  onSelect: (answer: boolean | undefined) => void;
  selectedAnswer: boolean | undefined;
};

type OptionalQuestionKey =
  | "wasOnTime"
  | "wasProperlyDressed"
  | "shouldComeAgain"
  | "followedInstructions";

const AdditionalQuestions = ({ question, onSelect, selectedAnswer }: AdditionalQuestionsProps) => {
  const { t } = useTranslation();

  const options = [
    { value: true, label: t("common.yes"), icon: "checkmark" as const },
    { value: false, label: t("common.no"), icon: "cross" as const },
  ];

  const renderOption = (option: (typeof options)[0]) => {
    const isSelected = selectedAnswer === option.value;

    const handlePress = () => {
      onSelect(isSelected ? undefined : option.value);
    };

    return (
      <Touchable
        key={option.label}
        onPress={handlePress}
        style={[yesNoStyles.option, isSelected && yesNoStyles.selectedOption]}
      >
        <Block
          style={
            isSelected
              ? option.value
                ? yesNoStyles.selectedCircleCheckmark
                : yesNoStyles.selectedCircle
              : yesNoStyles.circle
          }
        >
          {isSelected && (
            <SvgIcon
              name={option.icon}
              color={option.value ? palette.primary.accent : palette.primary.main}
              size={24}
              style={!option.value && yesNoStyles.crossIcon}
            />
          )}
        </Block>
        <Typography
          variant={isSelected ? "bodyBold" : "paragraph"}
          style={isSelected && yesNoStyles.selectedText}
        >
          {option.label}
        </Typography>
      </Touchable>
    );
  };

  return (
    <Block style={yesNoStyles.container}>
      <Typography center variant="bodyBold" style={yesNoStyles.question}>
        {question}
      </Typography>

      <Block row justifyCenter style={yesNoStyles.optionsContainer}>
        {options.map(renderOption)}
      </Block>
    </Block>
  );
};

const ReservationRating = () => {
  const { replace } = useRouter();
  const postHog = usePostHog();
  const { t } = useTranslation();
  const { reservationId = "" } = useLocalSearchParams<{ reservationId: string }>();

  const { data, loading } = useGetReservationByIdQuery({
    fetchPolicy: "network-only",
    variables: { orderId: reservationId, isCustomerApp: true },
  });
  const workers = data?.getReservationById?.workers;

  const [createReview, { loading: createReviewLoading }] = useCreateReviewMutation();

  const defaultValues = useMemo(
    () => ({
      rating: 0,
      comment: "",
      wasOnTime: undefined as boolean | undefined,
      wasProperlyDressed: undefined as boolean | undefined,
      shouldComeAgain: undefined as boolean | undefined,
      followedInstructions: undefined as boolean | undefined,
    }),
    [],
  );

  const { handleSubmit, setValue, watch } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
    values: defaultValues,
  });

  const values = watch();

  const isDisabled =
    loading || createReviewLoading || values?.rating === 0 || values?.comment === "";

  const setRatingHandler = useCallback(
    (newRating: number) => {
      setValue("rating", newRating, { shouldValidate: true });
    },
    [setValue],
  );

  const handleAnswerSelect = useCallback(
    (questionKey: OptionalQuestionKey, answer: boolean | undefined) => {
      setValue(questionKey, answer, { shouldValidate: true });
    },
    [setValue],
  );

  const additionalQuestions = {
    wasOnTime: t("screens.serviceRating.wasOnTime"),
    wasProperlyDressed: t("screens.serviceRating.wasPrepared"),
    shouldComeAgain: t("screens.serviceRating.wantTheSameWorkerAgain"),
    followedInstructions: t("screens.serviceRating.followedInstructions"),
  };

  const onSubmit = async (values: typeof defaultValues) => {
    const title = data?.getReservationById.bundle.name || "";

    try {
      await createReview({
        variables: {
          review: {
            orderId: reservationId,
            title,
            ...values,
          },
        },
      });
    } catch (_) {
      alert(t("alerts.cantUpdate"));
    } finally {
      postHog?.capture(values?.rating >= 4 ? AnalyticEvent.Review.Good : AnalyticEvent.Review.Bad, {
        orderId: reservationId,
        rating: values?.rating,
        comment: values?.comment,
        title,
      });
      replace(`/calendar/${reservationId}/rating/reviewSent`);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.select({ default: "padding", android: undefined })}
      keyboardVerticalOffset={useHeaderHeight() + 30}
    >
      <ScrollView contentContainerStyle={[styles.container, isWeb && smallPageWrapper]}>
        <Block row justifyCenter>
          {loading && (
            <Block alignCenter>
              <Block marginBottom={2}>
                <CirclePlaceholder size={12} />
              </Block>

              <LinePlaceholder width={20} height={3} />
            </Block>
          )}
          {workers?.map((worker) => (
            <Block key={worker?.id} alignCenter paddingHorizontal={1}>
              <Block marginBottom={2}>
                <Avatar size={spacing.unit * 12} uri={worker?.avatar} />
              </Block>
              <Typography center variant="paragraph">
                {worker?.name}
              </Typography>
            </Block>
          ))}
        </Block>

        <Block style={styles.reviewContainer}>
          <Typography center type="primary" variant="bodyBold">
            {t("screens.serviceRating.howDoYouRateYourExperience")}
          </Typography>

          <Block alignCenter marginLeft={-3}>
            <RatingScale
              currentRating={values?.rating}
              setRating={setRatingHandler}
              size={40}
              width={7}
            />
          </Block>
          <Block>
            {values?.rating === 0 ? (
              <Typography center type="secondary" variant="lightLabel">
                {t("screens.serviceRating.touchStarsToLeaveReview")}
              </Typography>
            ) : (
              <Typography center variant="lightLabel">
                {t(`screens.serviceRating.rating${values?.rating as 1 | 2 | 3 | 4 | 5}`)}
              </Typography>
            )}
          </Block>

          <Typography center variant="bodyBold" style={styles.reviewTitle}>
            {t("screens.serviceRating.addPublicReview")}
          </Typography>

          <Input
            multiline
            onChangeText={(value) =>
              setValue("comment", value, {
                shouldValidate: true,
              })
            }
            placeholder={t("screens.serviceRating.enterText")}
            value={values?.comment}
            inputStyle={styles.reviewInput}
          />
        </Block>

        <Typography center variant="paragraph">
          {t("screens.serviceRating.optional")}
        </Typography>
        <Typography
          center
          type="secondary"
          variant="lightLabel"
          style={styles.answersNotSharedText}
        >
          {t("screens.serviceRating.answersNotShared")}
        </Typography>

        {Object.entries(additionalQuestions).map(([key, value]) => (
          <AdditionalQuestions
            key={key}
            question={value}
            selectedAnswer={values[key as OptionalQuestionKey] as boolean | undefined}
            onSelect={(answer) => handleAnswerSelect(key as OptionalQuestionKey, answer)}
          />
        ))}
      </ScrollView>

      <Block style={footerStyle}>
        <Button
          disabled={isDisabled}
          label={t("screens.serviceRating.leaveOpinion")}
          loading={createReviewLoading}
          onPress={handleSubmit(onSubmit)}
          type="primary"
        />
      </Block>
    </KeyboardAvoidingView>
  );
};

export default ReservationRating;

const yesNoStyles = StyleSheet.create({
  container: {
    backgroundColor: palette.common.grayBackground,
    borderRadius: spacing.radius,
    padding: spacing.unit * 3,
    marginTop: spacing.unit * 2,
  },
  question: {
    marginBottom: spacing.unit * 2,
  },
  optionsContainer: {
    gap: spacing.unit * 2,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: palette.common.lightGray,
    paddingHorizontal: spacing.unit * 2,
    borderRadius: 6,
    height: spacing.unit * 5,
    width: spacing.unit * 12,
    justifyContent: "space-between",
  },
  selectedOption: {
    backgroundColor: palette.primary.accent,
  },
  circle: {
    width: 22,
    height: 22,
    borderRadius: 20,
    borderWidth: 1,
  },
  checkmarkIcon: {
    backgroundColor: palette.primary.main,
  },
  crossIcon: {
    backgroundColor: palette.primary.accent,
  },
  selectedCircle: {
    backgroundColor: palette.primary.accent,
  },
  selectedCircleCheckmark: {
    height: 22,
    width: 22,
    borderRadius: 20,
    backgroundColor: palette.primary.main,
    alignItems: "center",
    justifyContent: "center",
  },
  selectedText: {
    color: palette.common.white,
  },
});

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.unit * 3.5,
    paddingBottom: spacing.unit * 15,
  },
  reviewContainer: {
    backgroundColor: palette.common.grayBackground,
    borderRadius: spacing.radius,
    paddingHorizontal: spacing.unit * 3,
    paddingTop: spacing.unit * 3,
    paddingBottom: spacing.unit,
    marginVertical: spacing.unit * 2,
  },
  reviewTitle: {
    marginTop: spacing.unit * 4,
    marginBottom: spacing.unit * 2,
  },
  reviewInput: {
    borderColor: palette.common.black,
  },
  answersNotSharedText: {
    marginTop: spacing.unit * 0.5,
    marginBottom: spacing.unit,
  },
});
